
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ReferralCode {
  id: string;
  user_id: string;
  code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReferralClick {
  id: string;
  referral_code_id: string;
  ip_address?: string;
  user_agent?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  clicked_at: string;
}

export interface ReferralConversion {
  id: string;
  referral_code_id: string;
  referred_user_id: string;
  subscription_id?: string;
  subscription_type: string;
  commission_amount: number;
  status: 'pending' | 'confirmed' | 'paid' | 'cancelled';
  converted_at: string;
  confirmed_at?: string;
  payment_processed_at?: string;
}

export interface ReferralPayout {
  id: string;
  user_id: string;
  payout_method: 'usdc' | 'paypal';
  payout_address?: string;
  total_amount: number;
  conversion_ids: string[];
  status: 'pending' | 'processing' | 'completed' | 'failed';
  requested_at: string;
  processed_at?: string;
  transaction_hash?: string;
  paypal_transaction_id?: string;
  error_message?: string;
}

export interface ReferralAnalytics {
  id: string;
  user_id: string;
  month_year: string;
  total_clicks: number;
  total_conversions: number;
  total_commission: number;
  conversion_rate: number;
  top_referrer_bonus: number;
  created_at: string;
  updated_at: string;
}

export const useReferralSystem = () => {
  const queryClient = useQueryClient();

  // Get user's referral code
  const { data: referralCode, isLoading: isLoadingCode } = useQuery({
    queryKey: ['referral-code'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      return data as ReferralCode | null;
    },
  });

  // Create referral code - now using the fixed database function
  const createReferralCode = useMutation({
    mutationFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Call the database function to generate a unique code
      const { data: codeData, error: codeError } = await supabase
        .rpc('generate_referral_code');

      if (codeError) {
        console.error('Function error:', codeError);
        throw codeError;
      }

      const { data, error } = await supabase
        .from('referral_codes')
        .insert({
          user_id: user.user.id,
          code: codeData,
        })
        .select()
        .single();

      if (error) throw error;
      return data as ReferralCode;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-code'] });
      toast.success('Referral code created successfully!');
    },
    onError: (error: any) => {
      console.error('Create referral code error:', error);
      toast.error('Failed to create referral code: ' + error.message);
    },
  });

  // Get referral analytics
  const { data: analytics, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['referral-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_analytics')
        .select('*')
        .order('month_year', { ascending: false });

      if (error) throw error;
      return data as ReferralAnalytics[];
    },
  });

  // Get referral conversions
  const { data: conversions, isLoading: isLoadingConversions } = useQuery({
    queryKey: ['referral-conversions'],
    queryFn: async () => {
      if (!referralCode) return [];

      const { data, error } = await supabase
        .from('referral_conversions')
        .select('*')
        .eq('referral_code_id', referralCode.id)
        .order('converted_at', { ascending: false });

      if (error) throw error;
      return data as ReferralConversion[];
    },
    enabled: !!referralCode,
  });

  // Get referral payouts
  const { data: payouts, isLoading: isLoadingPayouts } = useQuery({
    queryKey: ['referral-payouts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_payouts')
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) throw error;
      return data as ReferralPayout[];
    },
  });

  // Request payout
  const requestPayout = useMutation({
    mutationFn: async ({ 
      payoutMethod, 
      payoutAddress, 
      conversionIds 
    }: { 
      payoutMethod: 'usdc' | 'paypal'; 
      payoutAddress: string; 
      conversionIds: string[] 
    }) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Calculate total amount
      const { data: conversionsData, error: convError } = await supabase
        .from('referral_conversions')
        .select('commission_amount')
        .in('id', conversionIds)
        .eq('status', 'confirmed');

      if (convError) throw convError;

      const totalAmount = conversionsData.reduce((sum, conv) => sum + conv.commission_amount, 0);

      const { data, error } = await supabase
        .from('referral_payouts')
        .insert({
          user_id: user.user.id,
          payout_method: payoutMethod,
          payout_address: payoutAddress,
          total_amount: totalAmount,
          conversion_ids: conversionIds,
        })
        .select()
        .single();

      if (error) throw error;
      return data as ReferralPayout;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-payouts'] });
      queryClient.invalidateQueries({ queryKey: ['referral-conversions'] });
      toast.success('Payout request submitted successfully!');
    },
    onError: (error: any) => {
      toast.error('Failed to request payout: ' + error.message);
    },
  });

  return {
    referralCode,
    isLoadingCode,
    createReferralCode,
    analytics,
    isLoadingAnalytics,
    conversions,
    isLoadingConversions,
    payouts,
    isLoadingPayouts,
    requestPayout,
  };
};

// Hook for tracking referral clicks
export const useReferralTracking = () => {
  const trackClick = useMutation({
    mutationFn: async (referralCode: string) => {
      const { data, error } = await supabase
        .from('referral_codes')
        .select('id')
        .eq('code', referralCode)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      const { error: clickError } = await supabase
        .from('referral_clicks')
        .insert({
          referral_code_id: data.id,
          ip_address: null, // You could get this from a service
          user_agent: navigator.userAgent,
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
        });

      if (clickError) throw clickError;
    },
  });

  return { trackClick };
};
